import React, { useCallback, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';

import Axios from '../../../networking/intraceptor';
import * as endPoints from '../../../networking/urlEndPoints';
import {
  MISMATCH_SCHEDULE_COUNT_EVENT,
  MISMATCH_SCHEDULE_OPEN_EVENT,
} from './mismatchScheduleReviewEvents';

const getRxNumber = (row) => row?.rx ?? row?.rxnumber ?? row?.rx_number ?? '';

const getPatientName = (row) => {
  const directName = row?.patient_name ?? row?.patient ?? row?.patientName;
  if (directName) {
    return directName;
  }

  return [row?.first_name ?? row?.firstname ?? row?.fname, row?.last_name ?? row?.lastname ?? row?.lname]
    .filter(Boolean)
    .join(' ');
};

const getDefaultScheduleQuantity = (row) => row?.schedule_dose ?? '';

// Nightly schedule push is bridge-aware: a row stays "Waiting for bridge"
// until the pharmacy's Connector is live and WinRx confirms the write.
const renderSentToWinrx = (row) => {
  const status = String(row?.winrx_send_status ?? '').trim();
  if (status === 'sent') {
    return (
      <div>
        <div className="text-sm font-semibold text-emerald-700">Sent</div>
        <div className="mt-1 text-xs text-gray-600">{row?.winrx_sent_at || '-'}</div>
      </div>
    );
  }
  if (status === 'skipped') {
    return (
      <div>
        <div className="text-sm font-semibold text-gray-500">Not sent</div>
        {row?.winrx_last_error ? <div className="mt-1 text-xs text-gray-600">{row.winrx_last_error}</div> : null}
      </div>
    );
  }
  if (status === 'awaiting_confirmation') {
    return (
      <div>
        <div className="text-sm font-semibold text-amber-600">Needs pharmacist confirmation</div>
        <div className="mt-1 text-xs text-gray-600">Missed the overnight window</div>
      </div>
    );
  }
  if (status === 'pending') {
    const attempts = Number(row?.winrx_attempts || 0);
    return (
      <div>
        <div className="text-sm font-semibold text-amber-600">{attempts > 0 ? 'Retrying' : 'Waiting for bridge'}</div>
        {row?.winrx_last_error ? <div className="mt-1 text-xs text-gray-600">{row.winrx_last_error}</div> : null}
      </div>
    );
  }
  return <span className="text-slate-500">-</span>;
};

const getRowKey = (row, index) => {
  const rxNumber = getRxNumber(row);
  return [
    rxNumber || index,
    row?.cycle_no ?? row?.cycle ?? '',
    row?.frequency ?? '',
    row?.din ?? '',
  ].join('-');
};

const normalizeRows = (payload) => {
  const rows = payload?.data?.data ?? payload?.data ?? payload;
  return Array.isArray(rows) ? rows : [];
};

const confirmScheduleDoseUpdate = async ({ rxNumber, quantityText }) => {
  const result = await Swal.fire({
    title: 'Confirm schedule dose update',
    text: `Send quantity ${quantityText} for RX ${rxNumber}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Update',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    confirmButtonColor: '#1A56DB',
    cancelButtonColor: '#64748b',
  });

  return result.isConfirmed;
};

const MismatchScheduleReviewManager = ({ pharmacyId, isPharmacist = false }) => {
  const [records, setRecords] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [manualQuantities, setManualQuantities] = useState({});
  const [rowFeedback, setRowFeedback] = useState({});
  const [savingRowKey, setSavingRowKey] = useState('');
  const previousOverflow = useRef(null);
  const latestFetchRequestId = useRef(0);
  const recordsRef = useRef([]);
  const [isExiting, setIsExiting] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const showDrawer = isOpen || isExiting;

  const publishCount = useCallback((count) => {
    window.__oatrxMismatchScheduleCount = count;
    window.dispatchEvent(new CustomEvent(MISMATCH_SCHEDULE_COUNT_EVENT, {
      detail: { count },
    }));
  }, []);

  const setMismatchRecords = useCallback((nextRecords) => {
    recordsRef.current = nextRecords;
    setRecords(nextRecords);
    publishCount(nextRecords.length);
  }, [publishCount]);

  const fetchMismatchRows = useCallback(async () => {
    if (!pharmacyId || !isPharmacist) {
      latestFetchRequestId.current += 1;
      setMismatchRecords([]);
      return [];
    }

    const requestId = latestFetchRequestId.current + 1;
    latestFetchRequestId.current = requestId;
    setIsLoading(true);
    setLoadError('');

    try {
      const response = await Axios.post(endPoints.accountability_schedule_rx, {
        eligible_only: true,
      });
      const nextRecords = normalizeRows(response?.data);

      if (requestId !== latestFetchRequestId.current) {
        return recordsRef.current;
      }

      setMismatchRecords(nextRecords);
      return nextRecords;
    } catch (error) {
      if (requestId !== latestFetchRequestId.current) {
        return recordsRef.current;
      }

      const message = error?.response?.data?.message || error?.message || 'Unable to load mismatch schedule records.';
      setLoadError(message);

      if (recordsRef.current.length === 0) {
        publishCount(0);
      }

      return recordsRef.current;
    } finally {
      if (requestId === latestFetchRequestId.current) {
        setIsLoading(false);
      }
    }
  }, [isPharmacist, pharmacyId, publishCount, setMismatchRecords]);

  useEffect(() => {
    if (!pharmacyId || !isPharmacist) {
      setMismatchRecords([]);
      setIsOpen(false);
      return undefined;
    }

    fetchMismatchRows();

    return undefined;
  }, [fetchMismatchRows, isPharmacist, pharmacyId, setMismatchRecords]);

  useEffect(() => {
    return () => {
      publishCount(0);
    };
  }, [publishCount]);

  useEffect(() => {
    const handleOpenRequest = () => {
      if (!isPharmacist) {
        return;
      }

      setIsOpen(true);
      fetchMismatchRows();
    };

    window.addEventListener(MISMATCH_SCHEDULE_OPEN_EVENT, handleOpenRequest);

    return () => {
      window.removeEventListener(MISMATCH_SCHEDULE_OPEN_EVENT, handleOpenRequest);
    };
  }, [fetchMismatchRows, isPharmacist]);

  const handleClose = useCallback(() => {
    setHasOpened(false);
    setIsExiting(true);
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClose, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    previousOverflow.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setIsExiting(false);
    setHasOpened(false);

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setHasOpened(true));
    });

    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (showDrawer) {
      return undefined;
    }

    if (previousOverflow.current !== null) {
      document.body.style.overflow = previousOverflow.current;
      previousOverflow.current = null;
    }

    return undefined;
  }, [showDrawer]);

  useEffect(() => {
    return () => {
      if (previousOverflow.current !== null) {
        document.body.style.overflow = previousOverflow.current;
        previousOverflow.current = null;
      }
    };
  }, []);

  const handleTransitionEnd = (event) => {
    if (event.target !== event.currentTarget || event.propertyName !== 'transform') {
      return;
    }

    if (isExiting) {
      setIsExiting(false);
    }
  };

  const handleOpenRx = (rxNumber) => {
    if (!rxNumber) {
      return;
    }

    window.location.href = `/pharmacy-investigate-rx?rx-number=${encodeURIComponent(rxNumber)}`;
  };

  const handleManualQuantityChange = (rowKey, value) => {
    setManualQuantities((previous) => ({ ...previous, [rowKey]: value }));
    setRowFeedback((previous) => ({ ...previous, [rowKey]: null }));
  };

  const updateScheduleDose = async ({ row, rowKey, quantity }) => {
    const rxNumber = getRxNumber(row);
    const quantityText = String(quantity ?? '').trim();
    const numericQuantity = Number(quantityText);

    if (!rxNumber) {
      setRowFeedback((previous) => ({
        ...previous,
        [rowKey]: { type: 'error', message: 'Rx number is required.' },
      }));
      return;
    }

    if (quantityText === '' || !Number.isFinite(numericQuantity) || numericQuantity < 0) {
      setRowFeedback((previous) => ({
        ...previous,
        [rowKey]: { type: 'error', message: 'Enter a numeric amount greater than or equal to 0.' },
      }));
      return;
    }

    const confirmed = await confirmScheduleDoseUpdate({ rxNumber, quantityText });

    if (!confirmed) {
      return;
    }

    setSavingRowKey(rowKey);
    setRowFeedback((previous) => ({ ...previous, [rowKey]: null }));

    try {
      await Axios.post(endPoints.save_schedule_dose, {
        rx: rxNumber,
        quantity: quantityText,
      });

      setRowFeedback((previous) => ({
        ...previous,
        [rowKey]: { type: 'success', message: 'Schedule dose updated.' },
      }));
      setRecords((previousRecords) => {
        const nextRecords = previousRecords.filter((previousRow, previousIndex) => (
          getRowKey(previousRow, previousIndex) !== rowKey
        ));
        recordsRef.current = nextRecords;
        publishCount(nextRecords.length);
        return nextRecords;
      });
      await fetchMismatchRows();
    } catch (error) {
      const message = error?.response?.data?.message || error?.response?.data?.error?.message || 'Unable to update schedule dose.';
      setRowFeedback((previous) => ({
        ...previous,
        [rowKey]: { type: 'error', message },
      }));
    } finally {
      setSavingRowKey('');
    }
  };

  if (!showDrawer) {
    return null;
  }

  const drawerTranslate = hasOpened && !isExiting ? 'translate-y-0' : 'translate-y-full';
  const backdropOpacity = hasOpened && !isExiting ? 'opacity-100' : 'opacity-0';

  return (
    <div className="fixed inset-0 z-[110] flex items-end">
      <div
        className={`absolute inset-0 bg-slate-900/35 backdrop-blur-[1px] transition-opacity duration-300 ease-out ${backdropOpacity}`}
        onClick={handleClose}
        aria-hidden
      />
      <section
        className={`relative max-h-[82vh] w-full rounded-t-2xl border-t border-slate-200 bg-white shadow-2xl transition-transform duration-300 ease-out ${drawerTranslate}`}
        onTransitionEnd={handleTransitionEnd}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Mismatch Schedule</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close mismatch schedule panel"
          >
            <span className="text-xl leading-none">&times;</span>
          </button>
        </div>

        <div className="max-h-[calc(82vh-81px)] overflow-y-auto bg-slate-50">
          {isLoading && records.length === 0 ? (
            <div className="px-6 py-10 text-sm text-slate-500">Loading mismatch schedule records...</div>
          ) : null}

          {loadError ? (
            <div className="px-6 py-5">
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                {loadError}
              </div>
            </div>
          ) : null}

          {!isLoading && !loadError && records.length === 0 ? (
            <div className="px-6 py-10 text-sm text-slate-500">
              No schedule-dose mismatches were found.
            </div>
          ) : null}

          {records.length > 0 ? (
            <div className="p-6">
              <div className="overflow-x-auto rounded-lg border border-gray-300 bg-white">
                <table className="w-full min-w-[1040px] table-fixed text-sm">
                  <thead className="bg-[#00578B] text-white">
                    <tr>
                      <th className="w-[7%] px-2 py-3 text-left font-semibold">RX</th>
                      <th className="w-[5%] px-2 py-3 text-center font-semibold">STATUS</th>
                      <th className="w-[13%] px-2 py-3 text-left font-semibold">PATIENT</th>
                      <th className="w-[22%] px-2 py-3 text-left font-semibold">DRUG / DIN</th>
                      <th className="w-[9%] px-2 py-3 text-center font-semibold">SCHEDULE DOSE</th>
                      <th className="w-[14%] px-2 py-3 text-center font-semibold">WINRX PROCESSING SCHEDULE</th>
                      <th className="w-[14%] px-2 py-3 text-center font-semibold">SENT TO WINRX</th>
                      <th className="w-[16%] px-2 py-3 text-left font-semibold">
                        <span className="sr-only">Update</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((row, index) => {
                      const rowKey = getRowKey(row, index);
                      const rxNumber = getRxNumber(row);
                      const statusText = String(row?.rxstat ?? row?.status ?? '').trim();
                      const patientName = getPatientName(row);
                      const drugText = [row?.drug, row?.din].filter(Boolean).join(' - ');
                      const feedback = rowFeedback[rowKey];
                      const isSaving = savingRowKey === rowKey;
                      const manualQuantity = manualQuantities[rowKey] ?? getDefaultScheduleQuantity(row);

                      return (
                        <tr key={rowKey} className="align-top border-b border-gray-300 bg-white hover:bg-gray-100">
                          <td className="px-2 py-3 font-medium">
                            {rxNumber ? (
                              <button
                                type="button"
                                onClick={() => handleOpenRx(rxNumber)}
                                className="text-left font-semibold text-red-600 underline-offset-2 hover:text-red-700 hover:underline"
                              >
                                {rxNumber}
                              </button>
                            ) : (
                              <span className="text-slate-500">-</span>
                            )}
                          </td>
                          <td className="px-2 py-3 text-center font-semibold text-gray-900">{statusText || '-'}</td>
                          <td className="px-2 py-3 text-gray-900 break-words">{patientName || '-'}</td>
                          <td className="px-2 py-3 text-gray-900 break-words">
                            {drugText || '-'}
                            {row?.frequency || row?.cycle_no ? (
                              <div className="mt-1 text-xs text-gray-600">
                                {[row?.frequency, row?.cycle_no ? `Cycle ${row.cycle_no}` : ''].filter(Boolean).join(' / ')}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-2 py-3 text-center font-semibold text-gray-900">{row?.processed_dose ?? '-'}</td>
                          <td className="px-2 py-3 text-center font-semibold text-red-600">{row?.scqty ?? '-'}</td>
                          <td className="px-2 py-3 text-center">{renderSentToWinrx(row)}</td>
                          <td className="px-2 py-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                step="any"
                                value={manualQuantity}
                                onChange={(event) => handleManualQuantityChange(rowKey, event.target.value)}
                                className="h-[42px] w-24 rounded-lg border border-gray-300 bg-white px-4 text-sm text-gray-900 outline-none placeholder:text-[#8c9ab5] focus:border-gray-300 focus:ring-0"
                                placeholder="Qty"
                                aria-label={`Manual schedule dose for RX ${rxNumber}`}
                              />
                              <button
                                type="button"
                                onClick={() => updateScheduleDose({ row, rowKey, quantity: manualQuantity })}
                                disabled={isSaving}
                                className={`h-9 rounded-md px-3 text-xs font-semibold text-white ${isSaving ? 'bg-[#1A56DB]/50 cursor-not-allowed' : 'bg-[#1A56DB] hover:bg-[#1851c8]'}`}
                              >
                                {isSaving ? 'Updating...' : 'Update to WinRx'}
                              </button>
                            </div>
                            {feedback?.message ? (
                              <p className={`mt-2 text-xs font-medium ${feedback.type === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>
                                {feedback.message}
                              </p>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default MismatchScheduleReviewManager;
